import _ from 'lodash';
import { snakeCasedOrgPartHelper } from 'utils/functions';
import {
  CORE,
  FilterLinkOperatorsValues,
  GenericResource,
  NullOperatorValues,
  OperatorMap,
  OrderDirectionValues,
} from 'types/api';
import { UnifiedFieldFilter, UnifiedQueryParams } from 'types/unified/api';

function isNullOperator(operator: string) {
  return Object.keys(NullOperatorValues).includes(operator);
}

function isFilterLinkOperator<T extends GenericResource>(
  filter: UnifiedFieldFilter<T> | keyof typeof FilterLinkOperatorsValues
) {
  return Object.keys(FilterLinkOperatorsValues).includes(filter as string);
}

function isFieldFilter<T extends GenericResource>(
  filter: UnifiedFieldFilter<T> | keyof typeof FilterLinkOperatorsValues
): filter is UnifiedFieldFilter<T> {
  return (filter as UnifiedFieldFilter<T>).field !== undefined;
}

function getNullOperatorValue(operator: string) {
  return NullOperatorValues[operator as keyof typeof NullOperatorValues];
}

function getOperator(operator: string) {
  return OperatorMap[operator as keyof typeof OperatorMap];
}

function parseNestedField(field: string | symbol | number) {
  return field
    .toString()
    .split('.')
    .reduce((acc, fieldPart, index) => {
      const snakeCasedFieldPart = snakeCasedOrgPartHelper(fieldPart);
      return `${acc}${index !== 0 ? '__' : ''}${snakeCasedFieldPart}`;
    }, '');
}

function getValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((item) => encodeURIComponent(item)).join(';');
  }
  return encodeURIComponent(String(value));
}

function getFilterExpression<T extends GenericResource>(
  filterItem: UnifiedFieldFilter<T>
) {
  const { field, operator, value } = filterItem;
  return `${parseNestedField(field)}${getOperator(operator)}:${
    isNullOperator(operator) ? getNullOperatorValue(operator) : getValue(value)
  }`;
}

function getFilterLinkOperator(
  operator: keyof typeof FilterLinkOperatorsValues
) {
  return FilterLinkOperatorsValues[operator];
}

function getFilterParam<T extends GenericResource>(
  filters: UnifiedQueryParams<T>['filters'] | UnifiedQueryParams<T>['excludes']
) {
  return filters
    ?.filter(
      (item) =>
        isFilterLinkOperator(item) ||
        (isFieldFilter(item) &&
          (!_.isNull(item.value) || isNullOperator(item.operator)) &&
          (isNullOperator(item.operator) || !_.isUndefined(item.value)) &&
          (_.isArray(item.value) ? item.value.length > 0 : true))
    )
    ?.reduce((acc: string[], item: unknown, index: number, originalArray) => {
      if (isFilterLinkOperator(item as any)) {
        if (acc.length === 0) {
          return acc;
        }
        acc[acc.length - 1] = `${acc[acc.length - 1]}${getFilterLinkOperator(
          item as keyof typeof FilterLinkOperatorsValues
        )}`;
        return acc;
      }

      if (acc.length === 0) {
        acc.push(getFilterExpression<T>(item as UnifiedFieldFilter<T>));
        return acc;
      }

      if (
        originalArray[index - 1] &&
        isFilterLinkOperator(originalArray[index - 1] as any)
      ) {
        if (acc.length === 0) {
          acc.push(getFilterExpression<T>(item as UnifiedFieldFilter<T>));
        } else {
          acc[acc.length - 1] = `${acc[acc.length - 1]}${getFilterExpression<T>(
            item as UnifiedFieldFilter<T>
          )}`;
        }
        return acc;
      }

      acc.push(getFilterExpression<T>(item as UnifiedFieldFilter<T>));
      return acc;
    }, [])
    ?.join(',');
}

function buildSchemaString(subSchema: unknown[]): string {
  return subSchema
    .map((item: unknown) => {
      if (typeof item === 'string' && _.includes(item, 'custom_properties')) {
        return `custom_properties__${_.chain(item)
          .replace('custom_properties__', '')
          .snakeCase()
          .value()}`;
      }
      if (typeof item === 'string') {
        const snakeCasedItem = snakeCasedOrgPartHelper(item);
        return item === CORE ? item : snakeCasedItem;
      }
      return Object.keys(item as object)
        .map((key) => {
          const snakeCasedKey = snakeCasedOrgPartHelper(key);
          const nestedSchema = (item as Record<string, unknown[]>)[key];
          // Empty array is shorthand for __core__ in nested fields
          const nestedSchemaString =
            Array.isArray(nestedSchema) && nestedSchema.length === 0
              ? CORE
              : buildSchemaString(nestedSchema);
          return `${snakeCasedKey}{${nestedSchemaString}}`;
        })
        .join(',');
    })
    .join(',');
}

function getPageNumberParam<T extends GenericResource>(
  pageNumber: UnifiedQueryParams<T>['pageNumber']
) {
  return `page_number=${pageNumber}`;
}

function getPageSizeParam<T extends GenericResource>(
  pageSize: UnifiedQueryParams<T>['pageSize']
) {
  return `page_size=${pageSize}`;
}

function getSortParam<T extends GenericResource>(
  sort: UnifiedQueryParams<T>['sort']
) {
  return `order_by=${sort?.map(
    (item) =>
      `${
        OrderDirectionValues[
          item.orderDirection as keyof typeof OrderDirectionValues
        ]
      }${parseNestedField(item.orderBy)}`
  )}`;
}

function getNoCacheParam<T extends GenericResource>(
  noCache: UnifiedQueryParams<T>['noCache']
) {
  return noCache ? 'no_cache' : '';
}

function getExportParam<T extends GenericResource>(
  exportBlob: UnifiedQueryParams<T>['exportBlob']
) {
  return exportBlob ? 'export' : '';
}

function getDistinctParam<T extends GenericResource>(
  distinct: UnifiedQueryParams<T>['distinct']
) {
  return distinct ? 'distinct' : '';
}

/**
 * Get schema parameter for Unified API queries.
 *
 * Differs from Client V2's getSchemaParam by not defaulting to __all__.
 * Unified API requires explicit schema specification.
 *
 * @param schema - The schema configuration from UnifiedQueryParams
 * @returns Query string parameter for schema
 */
export function getUnifiedSchemaParam<T extends GenericResource>(
  schema: UnifiedQueryParams<T>['schema']
) {
  if (!schema) {
    return undefined;
  }

  if (typeof schema === 'string') {
    return `schema=${schema}`;
  }

  if (Array.isArray(schema)) {
    return `schema=${buildSchemaString(schema)}`;
  }

  return `schema=${String(schema)}`;
}

/**
 * Get search schema parameter for Unified API queries.
 *
 * Differs from Client V2's getSearchSchemaParam by not defaulting to __all__.
 *
 * @param schema - The search schema configuration from UnifiedQueryParams
 * @returns Query string parameter for search_schema
 */
export function getUnifiedSearchSchemaParam<T extends GenericResource>(
  schema: UnifiedQueryParams<T>['searchSchema']
) {
  if (!schema) {
    return undefined;
  }

  if (typeof schema === 'string') {
    return `search_schema=${schema}`;
  }

  if (Array.isArray(schema)) {
    return `search_schema=${buildSchemaString(schema)}`;
  }

  return `search_schema=${String(schema)}`;
}

/**
 * Parses UnifiedQueryParams into a query string for Unified API endpoints.
 *
 * Key differences from parseQueryParams (Client V2):
 * - No support for newVersion parameter (Client V2 only)
 * - No support for blended parameter (Client V2 only)
 * - Schema is required (no __all__ default)
 * - Supports __core__ keyword in schema
 * - Supports unlimited nesting depth in filters and schema
 *
 * @param params - UnifiedQueryParams object
 * @returns Query string starting with '?'
 *
 * @example
 * parseUnifiedQueryParams({
 *   schema: [CORE, { lines: [CORE] }],
 *   filters: [{ field: 'lines.part.mpn', operator: 'contains', value: 'ABC' }],
 *   pageSize: 25
 * })
 * // Returns: '?page_size=25&filters=...&schema=__core__,lines{__core__}'
 */
export function parseUnifiedQueryParams<T extends GenericResource>({
  pageSize,
  pageNumber = 1,
  sort,
  filters,
  excludes,
  schema,
  searchSchema,
  search,
  noCache = false,
  exportBlob = false,
  distinct = false,
}: UnifiedQueryParams<T>) {
  const queryParams: string[] = [];

  if (pageSize !== undefined) {
    queryParams.push(getPageSizeParam<T>(pageSize));
  }
  if (pageNumber) {
    queryParams.push(getPageNumberParam<T>(pageNumber));
  }
  if (sort && sort.length > 0) {
    queryParams.push(getSortParam<T>(sort));
  }
  if (excludes && excludes?.length > 0) {
    const excludesParam = getFilterParam<T>(excludes);
    if (excludesParam) {
      queryParams.push(`excludes=${excludesParam}`);
    }
  }
  if (filters && filters?.length > 0) {
    const filtersParam = getFilterParam<T>(filters);
    if (filtersParam) {
      queryParams.push(`filters=${filtersParam}`);
    }
  }
  if (searchSchema) {
    const searchSchemaParam = getUnifiedSearchSchemaParam<T>(searchSchema);
    if (searchSchemaParam) {
      queryParams.push(searchSchemaParam);
    }
  }
  if (search) {
    queryParams.push(`search=${encodeURIComponent(search)}`);
  }
  if (noCache) {
    queryParams.push(getNoCacheParam<T>(noCache));
  }
  if (exportBlob) {
    queryParams.push(getExportParam<T>(exportBlob));
  }
  if (distinct) {
    queryParams.push(getDistinctParam<T>(distinct));
  }

  // Schema is required for Unified API (no default)
  if (schema) {
    const schemaParam = getUnifiedSchemaParam<T>(schema);
    if (schemaParam) {
      queryParams.push(schemaParam);
    }
  }

  return `?${queryParams.join('&')}`;
}
